import React, { useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from '../../app/hooks'
import { pickAdminUser, resetAdminUser, saveAdminUserAsync, selectAdminUser, selectAllAdminUsers, selectAllUserRoles } from './AdminUserSlice'
import { Autocomplete, Button, Chip, FormControl, FormHelperText, TextField, Typography } from '@mui/material'
import { epochToDate } from '../utils/DateUtil'
import { Check, Warning, WarningAmber } from '@mui/icons-material'
import { grey } from '@mui/material/colors'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'

const AdminUserEditor = () => {
  const { uid } = useParams()
  const dispatch = useAppDispatch()
  const navigate = useNavigate()

  const adminUsers = useAppSelector(selectAllAdminUsers)
  const selectedAdminUser = useAppSelector(selectAdminUser)
  const allAvailableUserRoles = useAppSelector(selectAllUserRoles)

  const [userRoles, setUserRoles] = useState<any[]>([])

  const [paidTier, setPaidTier] = useState<any>('')
  const [paidTierCount, setPaidTierCount] = useState<any>('')
  const [paidTierExpiry, setPaidTierExpiry] = useState<any>('')

  const paidTierIp = useRef<any>(null)
  const paidTierCountIp = useRef<any>(null)
  const paidTierExpiryIp = useRef<any>(null)

  useEffect(() => {
    const selectedAdminUser = adminUsers.content.find((u) => u.userId === uid)
    if (selectedAdminUser) {
      dispatch(pickAdminUser(selectedAdminUser))
      setUserRoles(selectedAdminUser.role?.split(',').map(r => r.trim()))
      setPaidTier(selectedAdminUser.paidTier)
      setPaidTierCount(selectedAdminUser.paidTierCount)
      setPaidTierExpiry(selectedAdminUser.paidTierExpiry * 1000)
    }
  }, [uid, adminUsers])

  return (<div>
    {selectedAdminUser && <>
      <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
        Editing {selectedAdminUser.userEmail}
      </Typography>
      <Typography variant="caption" component="div" sx={{ flexGrow: 1, mb: 4, color: grey[500] }}>
        {selectedAdminUser.userId}
      </Typography>

      <FormControl fullWidth={true} required={true} sx={{ marginBottom: 2 }}>
        <TextField id="ip-text-paid-tier" label="Paid tier identifier" variant="outlined"
          name="paid-tier" type="number" ref={paidTierIp} value={paidTier ?? ''}
          onChange={(e: any) => setPaidTier(e.target.value)} />
        <FormHelperText id="helper-text-paid-tier">Paid tier</FormHelperText>
      </FormControl>

      <FormControl fullWidth={true} required={true} sx={{ marginBottom: 2 }}>
        <TextField id="ip-text-paid-tier-count" label="Number of properties remaining" variant="outlined"
          name="paid-tier-count" onChange={(e: any) => setPaidTierCount(e.target.value)}
          ref={paidTierCountIp} value={paidTierCount ?? ''} />
        <FormHelperText id="helper-text-paid-tier-count">Number of reports allowed</FormHelperText>
      </FormControl>

      <FormControl fullWidth={true} required={true} sx={{ marginBottom: 2 }}>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <DatePicker label="Expires on"
            onChange={(newValue: any) => setPaidTierExpiry(newValue)}
            ref={paidTierExpiryIp} value={paidTierExpiry ?? 0} />
        </LocalizationProvider>
        <FormHelperText id="helper-text-paid-tier-expiry">Paid Tier expires on</FormHelperText>
      </FormControl>

      <Autocomplete
        sx={{ marginBottom: 2 }}
        multiple
        id="ip-ac-user-roles"
        options={allAvailableUserRoles}
        value={userRoles}
        onChange={(_, newValues) => setUserRoles(newValues)}
        renderInput={(params) => (
          <TextField
            {...params}
            label="User Roles"
            variant="outlined"
          />
        )}
        renderTags={(value, getTagProps) =>
          value.map((option, index) => (
            <Chip label={option} {...getTagProps({ index })} key={index} />
          ))
        }
      />

      <Button variant="outlined" endIcon={<Check />} sx={{ marginRight: 2 }} onClick={() => {
        dispatch(saveAdminUserAsync({
          adminUser: {
            ...selectedAdminUser,
            paidTier: paidTier,
            paidTierCount: paidTierCount,
            paidTierExpiry: (paidTierExpiry instanceof Date) ? paidTierExpiry.getTime() / 1000 : (paidTierExpiry / 1000),
            role: (userRoles && userRoles.length) ? userRoles.join(',').trim() : 'ROLE_ANON'
          },
          mode: 'regular'
        }))
        navigate({ 'pathname': `/admin/users` })
      }}>Save</Button>

      <Button variant="outlined" color={selectedAdminUser.role?.indexOf('ROLE_USER') < 0 ? `success` : `warning`} endIcon={<WarningAmber />} sx={{ marginRight: 2 }} onClick={() => {
        const roles = selectedAdminUser.role?.replaceAll(' ', '')?.split(',')
        if (roles.includes('ROLE_USER')) {
          const rolesIndex = roles.indexOf('ROLE_USER')
          if (rolesIndex !== -1) {
            roles.splice(rolesIndex, 1);
          }
        } else {
          roles.push('ROLE_USER')
        }

        dispatch(saveAdminUserAsync({
          adminUser: {
            ...selectedAdminUser,
            role: roles.join(',').trim()
          },
          mode: 'regular'
        }))
        navigate({ 'pathname': `/admin/users` })
      }}>{selectedAdminUser.role?.indexOf('ROLE_USER') < 0 ? `Enable` : `Disable`}</Button>

      <Button variant="outlined" sx={{ marginRight: 2 }} onClick={() => {
        dispatch(resetAdminUser())
        navigate({ 'pathname': `/admin/users` })
      }}>Cancel</Button>
    </>}
  </div>)
}

export default AdminUserEditor